import { AIChatProvider } from 'components/organisms/AIChat/AIChat.context';
import { useAiMetadata } from 'contexts/AIMetadataProvider';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { memo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import ResizeObserver from 'resizeObserver';
import type { ConversationMetadataType } from 'types/ai.types';
import { logAmplitude } from 'utils/amplitude';
import { WEB_AIASSISTANT_USEROPENOCUMENTCHAT } from 'utils/amplitude/events/ai.amplitude';
import AIHelper from './AIHelper.component';
import AIHelperChat from './AIHelperChat';

const DEFAULT_OFFSET = 48;

const EXCLUDED_DOCUMENT_TYPES: ConversationMetadataType[] = [
  'wrapped_2024',
  'email',
  'image',
  'pdf',
];

const AIHelperDocumentChat = (): JSX.Element | null => {
  const { data: user } = useGetCurrentCustomerData();
  const { t } = useTranslation();

  const {
    setToastOffset,
    metadata,
    clearMetadata,
    isHelperOpen,
    setIsHelperOpen,
  } = useAiMetadata();
  const ref = useRef<HTMLDivElement>(null);

  const handleResize = useCallback(() => {
    const height =
      !ref.current || !isHelperOpen
        ? DEFAULT_OFFSET
        : ref.current.offsetHeight + 24;
    setToastOffset(height);
  }, [setToastOffset, isHelperOpen]);

  useEffect(() => {
    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    if (ref.current) resizeObserver.observe(ref.current);

    return () => {
      ref.current && resizeObserver.unobserve(ref.current);
      setToastOffset(0);
    };
  }, [handleResize, setToastOffset]);

  const location = useLocation();
  useEffect(() => clearMetadata(), [location]);

  const toggleVisible = useCallback(() => {
    if (!isHelperOpen && metadata?.documentType) {
      logAmplitude(WEB_AIASSISTANT_USEROPENOCUMENTCHAT, {
        documentType: metadata?.documentType,
      });
    }

    setIsHelperOpen(!isHelperOpen);
  }, [isHelperOpen]);

  useEffect(() => {
    if (!metadata?.documentType) {
      setIsHelperOpen(false);
    }
  }, [metadata?.documentType, setIsHelperOpen]);

  if (
    !metadata?.documentType ||
    EXCLUDED_DOCUMENT_TYPES.includes(metadata?.documentType)
  )
    return null;

  const content = (
    <AIHelper
      isVisible={isHelperOpen}
      onVisibilityChange={toggleVisible}
      tooltip={t('ai_bot.tooltip.ask_ai')}
      animationOrigin="bottom-left"
      className="fixed bottom-6 left-6 z-[10000]"
      size="large"
      hideOpenButton
    >
      <AIChatProvider>
        <div ref={ref}>
          <AIHelperChat
            onClose={() => setIsHelperOpen(false)}
            isExpandable
            initialMessage={t(
              `ai_bot.document_chat.initial_message.${metadata?.documentType}`,
              { firstName: user?.firstName },
            )}
          />
        </div>
      </AIChatProvider>
    </AIHelper>
  );

  return createPortal(content, document.querySelector('#ai-chat-portal')!);
};

export default memo(AIHelperDocumentChat);
